import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { authAxios } from '../../contexts/AuthContext';
import { Package, RotateCcw, Check, X, Truck, Search, ShieldCheck, MessageCircle, Mail, RefreshCw, Printer, AlertTriangle } from 'lucide-react';
import { useBizInfo } from '../../contexts/BizInfoContext'; // ← ADD
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import OrderBill from '../../components/OrderBill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const returnStatusConfig = {
  return_requested:         { icon: RotateCcw,  label: 'Return Requested',  labelHi: 'वापसी अनुरोधित',   color: 'bg-yellow-100 text-yellow-800' },
  return_approved:          { icon: Check,       label: 'Return Approved',   labelHi: 'वापसी स्वीकृत',    color: 'bg-blue-100 text-blue-800' },
  return_product_picked_up: { icon: Truck,       label: 'Product Picked Up', labelHi: 'उत्पाद उठाया गया', color: 'bg-purple-100 text-purple-800' },
  return_quality_check:     { icon: Search,      label: 'Quality Check',     labelHi: 'गुणवत्ता जांच',    color: 'bg-indigo-100 text-indigo-800' },
  return_refund_processed:  { icon: ShieldCheck, label: 'Refund Processed',  labelHi: 'रिफंड प्रोसेस्ड',  color: 'bg-green-100 text-green-800' },
  return_rejected:          { icon: X,           label: 'Return Rejected',   labelHi: 'वापसी अस्वीकृत',   color: 'bg-red-100 text-red-800' }
};

const ReturnBadge = ({ status, language }) => {
  const cfg = returnStatusConfig[status];
  if (!cfg) return <Badge className="bg-slate-100 text-slate-800">{status}</Badge>;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {language === 'hi' ? cfg.labelHi : cfg.label}
    </Badge>
  );
};

// Builds a lookup of which items are in the return request
const buildReturnItemsMap = (returnItems = []) => {
  const map = {};
  for (const ri of returnItems) {
    map[ri.product_id] = ri;
  }
  return map;
};

export default function AdminReturns() {
  const { language, token } = useAuth();
  const { bizInfo } = useBizInfo(); // ← ADD
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState(null);
  const [billOrder, setBillOrder]       = useState(null);
  const [thermalMode, setThermalMode]   = useState(true); // 80mm thermal default

  const fetchReturns = useCallback(async () => {
    try {
      const response = await authAxios.get(`${API}/admin/returns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReturns(response.data);
    } catch (error) {
      toast.error('Failed to fetch returns');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);

  const handleStatusUpdate = async () => {
    if (!statusUpdate) {
      toast.error('Please select a status');
      return;
    }
    setUpdating(true);
    try {
      const response = await authAxios.put(
        `${API}/admin/orders/${selectedReturn.id}/return-status`,
        { return_status: statusUpdate, admin_notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        <div>
          <p>{language === 'hi' ? 'वापसी स्थिति अपडेट की गई' : 'Return status updated'}</p>
          <p className="text-xs mt-1">{language === 'hi' ? 'ईमेल सूचना भेजी गई' : 'Email notification sent'}</p>
        </div>
      );

      if (response.data?.whatsapp_link) {
        setWhatsappLink(response.data.whatsapp_link);
      } else {
        const phone = selectedReturn.phone || '';
        const phoneClean = phone.replace(/\D/g, '');
        const phoneWithCode = phoneClean.startsWith('91') ? phoneClean : `91${phoneClean}`;
        const statusLabel = returnStatusConfig[statusUpdate]?.label || statusUpdate;
        const message = encodeURIComponent(
          `Hello ${selectedReturn.user_name || 'Customer'},\n\nYour return request for Order #${selectedReturn.id.slice(0, 8)} has been updated.\n\nReturn Status: ${statusLabel}\n\n${adminNotes ? `Note: ${adminNotes}\n\n` : ''}Thank you,\nAV Mart Shamli`
        );
        setWhatsappLink(`https://wa.me/${phoneWithCode}?text=${message}`);
      }

      setSelectedReturn(null);
      setStatusUpdate('');
      setAdminNotes('');
      fetchReturns();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenWhatsapp = () => {
    if (whatsappLink) {
      window.open(whatsappLink, '_blank', 'noopener,noreferrer');
      setWhatsappLink(null);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">
          {language === 'hi' ? 'रिटर्न अनुरोध' : 'Return Requests'}
        </h1>
      <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
            <input type="checkbox" checked={thermalMode} onChange={e => setThermalMode(e.target.checked)} className="w-3.5 h-3.5" />
            80mm Thermal
          </label>
          <Button variant="outline" size="sm" onClick={fetchReturns}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {language === 'hi' ? 'रिफ्रेश' : 'Refresh'}
          </Button>
        </div>
      </div>

      {returns.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">
            {language === 'hi' ? 'कोई रिटर्न अनुरोध नहीं' : 'No return requests'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {returns.map((order) => {
            // Build a map of product_id → return item for quick lookup
            const returnItemsMap = buildReturnItemsMap(order.return_items || []);
            const isPartialReturn = order.is_partial_return;

            // Separate items into: being returned vs not being returned
            const itemsBeingReturned = (order.return_items && order.return_items.length > 0)
              ? order.return_items
              : order.items?.filter(i => !i.skipped) ?? [];

            const itemsNotReturned = order.items?.filter(
              i => !i.skipped && !returnItemsMap[i.product_id]
            ) ?? [];

            return (
              <div key={order.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-lg">Order #{order.id.slice(0, 8)}</h3>
                      {isPartialReturn && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">
                          {language === 'hi' ? 'आंशिक वापसी' : 'Partial Return'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      {language === 'hi' ? 'ग्राहक: ' : 'Customer: '}
                      <span className="font-medium text-slate-700">{order.user_name}</span>
                      {order.phone && <span className="ml-1">({order.phone})</span>}
                    </p>
                    <p className="text-sm text-slate-500">
                      {language === 'hi' ? 'ऑर्डर तिथि: ' : 'Order Date: '}
                      {formatDate(order.created_at)}
                    </p>
                  </div>
                  <div className="text-right space-y-2">
                    <ReturnBadge status={order.return_status} language={language} />
                    <p className="text-lg font-bold text-slate-900">₹{order.total.toFixed(0)}</p>
                  </div>
                </div>

                {/* ── Items Being Returned ── */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <RotateCcw className="w-4 h-4 text-orange-500" />
                    <h4 className="font-semibold text-sm text-orange-700">
                      {language === 'hi' ? 'वापस किए जाने वाले आइटम' : 'Items Being Returned'}
                      <span className="ml-1 text-orange-500">({itemsBeingReturned.length})</span>
                    </h4>
                  </div>
                  <div className="border border-orange-200 rounded-lg bg-orange-50 divide-y divide-orange-100">
                    {itemsBeingReturned.map((item, idx) => {
                      // Try to find image from original order items if return_items don't have it
                      const originalItem = order.items?.find(i => i.product_id === item.product_id);
                      const image = item.image || originalItem?.image;
                      return (
                        <div key={idx} className="flex items-center gap-3 p-3 text-sm">
                          {image ? (
                            <img src={image} alt={item.name} className="w-12 h-12 object-cover rounded border border-orange-200" />
                          ) : (
                            <div className="w-12 h-12 rounded bg-orange-100 flex items-center justify-center">
                              <Package className="w-5 h-5 text-orange-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">{item.name}</p>
                            <p className="text-slate-500 text-xs">
                              {language === 'hi' ? 'मात्रा: ' : 'Qty: '}{item.quantity} × ₹{item.price}
                            </p>
                          </div>
                          <span className="font-semibold text-orange-700">
                            ₹{(item.quantity * item.price).toFixed(0)}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Return reason from latest return_history entry */}
                  {order.return_history?.[0]?.reason && (
                    <div className="mt-2 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium">
                          {language === 'hi' ? 'वापसी का कारण: ' : 'Return Reason: '}
                        </span>
                        {order.return_history[0].reason}
                        {order.return_history[0].description && (
                          <p className="text-amber-700 mt-0.5">{order.return_history[0].description}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Customer uploaded images */}
                  {order.return_history?.[0]?.images?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-slate-500 mb-1">
                        {language === 'hi' ? 'ग्राहक द्वारा अपलोड की गई तस्वीरें:' : 'Customer uploaded images:'}
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {order.return_history[0].images.map((img, i) => (
                          <img key={i} src={img} alt={`Return ${i + 1}`}
                            className="w-16 h-16 object-cover rounded border cursor-pointer hover:scale-105 transition-transform"
                            onClick={() => window.open(img, '_blank')}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Items NOT Being Returned (only shown for partial returns) ── */}
                {isPartialReturn && itemsNotReturned.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="w-4 h-4 text-slate-400" />
                      <h4 className="font-semibold text-sm text-slate-500">
                        {language === 'hi' ? 'वापस नहीं किए जाने वाले आइटम' : 'Items NOT Being Returned'}
                        <span className="ml-1 text-slate-400">({itemsNotReturned.length})</span>
                      </h4>
                    </div>
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                      {itemsNotReturned.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 text-sm opacity-60">
                          <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                          <div className="flex-1">
                            <p className="font-medium text-slate-700">{item.name}</p>
                            <p className="text-slate-400 text-xs">Qty: {item.quantity} × ₹{item.price}</p>
                          </div>
                          <span className="text-slate-500">₹{(item.quantity * item.price).toFixed(0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Return History Timeline */}
                {order.return_history && order.return_history.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold mb-3 text-sm">
                      {language === 'hi' ? 'रिटर्न का इतिहास' : 'Return History'}
                    </h4>
                    <div className="space-y-3">
                      {order.return_history.map((entry, idx) => {
                        const entryCfg = returnStatusConfig[entry.status];
                        const EntryIcon = entryCfg?.icon || RotateCcw;
                        return (
                          <div key={idx} className="flex gap-3">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${entryCfg?.color || 'bg-slate-100 text-slate-600'}`}>
                              <EntryIcon className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 pb-3 border-b border-slate-200 last:border-0">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">
                                  {language === 'hi' ? entryCfg?.labelHi : entryCfg?.label || entry.status}
                                </span>
                                <span className="text-slate-500 text-xs">{formatDate(entry.timestamp)}</span>
                              </div>
                              {entry.admin_notes && (
                                <div className="mt-1 p-2 bg-amber-50 rounded text-sm text-amber-800">
                                  <strong>{language === 'hi' ? 'एडमिन नोट: ' : 'Admin Note: '}</strong>{entry.admin_notes}
                                </div>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                By: {entry.updated_by_name || entry.updated_by}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setBillOrder(order)}
                    className="flex-shrink-0"
                    title={language === 'hi' ? 'बिल प्रिंट करें' : 'Print Bill'}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    {language === 'hi' ? 'बिल' : 'Bill'}
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedReturn(order);
                      setStatusUpdate(order.return_status);
                      setAdminNotes('');
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {language === 'hi' ? 'स्थिति अपडेट करें' : 'Update Return Status'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Update Status Dialog ── */}
      <Dialog open={!!selectedReturn} onOpenChange={() => setSelectedReturn(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'hi' ? 'रिटर्न स्थिति अपडेट करें' : 'Update Return Status'}
            </DialogTitle>
          </DialogHeader>

          {selectedReturn && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium">Order #{selectedReturn.id.slice(0, 8)}</p>
                <p className="text-slate-500">{selectedReturn.user_name} — ₹{selectedReturn.total.toFixed(0)}</p>
                {selectedReturn.return_items?.length > 0 && (
                  <p className="text-orange-600 text-xs mt-1">
                    {selectedReturn.return_items.length} item(s) in return request
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {language === 'hi' ? 'नई स्थिति' : 'New Status'} *
                </label>
                <Select value={statusUpdate} onValueChange={setStatusUpdate}>
                  <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(returnStatusConfig).map(([val, cfg]) => (
                      <SelectItem key={val} value={val}>
                        {language === 'hi' ? cfg.labelHi : cfg.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {language === 'hi' ? 'एडमिन नोट्स (वैकल्पिक)' : 'Admin Notes (Optional)'}
                </label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this status update..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedReturn(null)}>
              {language === 'hi' ? 'रद्द करें' : 'Cancel'}
            </Button>
            <Button onClick={handleStatusUpdate} disabled={updating} className="bg-green-600 hover:bg-green-700">
              {updating
                ? language === 'hi' ? 'अपडेट हो रहा है...' : 'Updating...'
                : language === 'hi' ? 'अपडेट करें' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── WhatsApp Notification Dialog ── */}
      <Dialog open={!!whatsappLink} onOpenChange={() => setWhatsappLink(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-500" />
              {language === 'hi' ? 'WhatsApp पर सूचना भेजें' : 'Send WhatsApp Notification'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
              <Mail className="w-5 h-5 text-green-600" />
              <span className="text-green-700 text-sm">
                {language === 'hi' ? 'ईमेल सूचना स्वचालित रूप से भेजी गई!' : 'Email notification sent automatically!'}
              </span>
            </div>
            <p className="text-sm text-slate-600">
              {language === 'hi' ? 'ग्राहक को WhatsApp पर भी सूचित करने के लिए नीचे बटन पर क्लिक करें:' : 'Click below to also notify the customer via WhatsApp:'}
            </p>
            <Button onClick={handleOpenWhatsapp} className="flex items-center justify-center gap-2 w-full py-3 bg-green-500 hover:bg-green-600 text-white" data-testid="whatsapp-notify-btn">
              <MessageCircle className="w-5 h-5" />
              {language === 'hi' ? 'WhatsApp पर भेजें' : 'Send on WhatsApp'}
            </Button>
            <Button variant="outline" className="w-full" onClick={() => setWhatsappLink(null)}>
              {language === 'hi' ? 'बाद में' : 'Skip for now'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Bill Dialog ── */}
      <OrderBill
        order={billOrder}
        open={!!billOrder}
        onClose={() => setBillOrder(null)}
        isAdmin={true}
        language={language}
        bizInfo={bizInfo}
        thermalMode={thermalMode}
      />
    </div>
  );
}
